import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { calculateBoundingBox } from "./utils/domUtils";

const HIGHLIGHT_PADDING = 8; // Padding around the highlighted area in pixels
const HIGHLIGHT_BORDER_WIDTH = 3; // Width of the animated gradient border in pixels

interface HoverHighlightProps {
    target: string | string[]; // Element(s) to highlight - string for single selector, array for multiple
    hoverTriggerRef?: React.RefObject<HTMLElement>; // Element to attach hover listeners to
    noPadding?: boolean;
}

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const pulseGradient = keyframes`
    0% { border-color: #A40D94; }
    50% { border-color: #8D31E6; }
    100% { border-color: #A40D94; }
`;

const CustomHighlightOverlay = styled.div<{ boundingBox: { top: number; left: number; width: number; height: number } }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    pointer-events: none;
    animation: ${fadeIn} 600ms ease-out forwards;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        clip-path: polygon(
            0% 0%,
            0% 100%,
            ${props => props.boundingBox.left}px 100%,
            ${props => props.boundingBox.left}px ${props => props.boundingBox.top}px,
            ${props => props.boundingBox.left + props.boundingBox.width}px ${props => props.boundingBox.top}px,
            ${props => props.boundingBox.left + props.boundingBox.width}px ${props => props.boundingBox.top + props.boundingBox.height}px,
            ${props => props.boundingBox.left}px ${props => props.boundingBox.top + props.boundingBox.height}px,
            ${props => props.boundingBox.left}px 100%,
            100% 100%,
            100% 0%
        );
    }
    
    &::after {
        content: '';
        position: absolute;
        top: ${props => props.boundingBox.top}px;
        left: ${props => props.boundingBox.left}px;
        width: ${props => props.boundingBox.width}px;
        height: ${props => props.boundingBox.height}px;
        border: ${HIGHLIGHT_BORDER_WIDTH}px solid #A40D94;
        border-radius: 2px;
        box-sizing: border-box;
        pointer-events: none;
        animation: ${pulseGradient} 1600ms linear infinite;
    }
`;

export const HoverHighlight: React.FC<HoverHighlightProps> = ({ target, hoverTriggerRef, noPadding }) => {
    const [isHovering, setIsHovering] = useState(false);
    const [boundingBox, setBoundingBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

    const hoverElementRef = useRef<HTMLElement | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const attachListenersTimeoutRef = useRef<number | null>(null);

    const updateBoundingBox = useCallback(() => {
        const boundingBox = calculateBoundingBox(target, noPadding ? 0 : HIGHLIGHT_PADDING);
        setBoundingBox(boundingBox);
    }, [target, noPadding]);

    const startBoundingBoxUpdates = useCallback(() => {
        const updateLoop = () => {
            updateBoundingBox();
            animationFrameRef.current = requestAnimationFrame(updateLoop);
        };
        animationFrameRef.current = requestAnimationFrame(updateLoop);
    }, [updateBoundingBox]);

    const stopBoundingBoxUpdates = useCallback(() => {
        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    const handleMouseEnter = useCallback(() => {
        // Verify target element(s) exist before showing highlight
        const boundingBox = calculateBoundingBox(target, noPadding ? 0 : HIGHLIGHT_PADDING);
        if (boundingBox) {
            setIsHovering(true);
            setBoundingBox(boundingBox);

            // Update bounding box on each animation frame in case elements move/resize
            startBoundingBoxUpdates();
        }
    }, [target, noPadding, startBoundingBoxUpdates]);

    const handleMouseLeave = useCallback(() => {
        setIsHovering(false);
        setBoundingBox(null);
        stopBoundingBoxUpdates();
    }, [stopBoundingBoxUpdates]);

    const detachListeners = useCallback(() => {
        if (hoverElementRef.current) {
            hoverElementRef.current.removeEventListener("mouseenter", handleMouseEnter);
            hoverElementRef.current.removeEventListener("mouseleave", handleMouseLeave);
            hoverElementRef.current = null;
        }
    }, [handleMouseEnter, handleMouseLeave]);

    // Effect to attach/detach listeners when hoverTriggerRef changes
    useEffect(() => {
        // Clear any pending attach timeout
        if (attachListenersTimeoutRef.current !== null) {
            clearTimeout(attachListenersTimeoutRef.current);
            attachListenersTimeoutRef.current = null;
        }

        detachListeners();

        const attachListeners = () => {
            if (hoverTriggerRef?.current) {
                hoverElementRef.current = hoverTriggerRef.current;
                hoverElementRef.current.addEventListener("mouseenter", handleMouseEnter);
                hoverElementRef.current.addEventListener("mouseleave", handleMouseLeave);
            } else {
                // Retry if ref not ready yet
                attachListenersTimeoutRef.current = window.setTimeout(() => {
                    attachListenersTimeoutRef.current = null;
                    attachListeners();
                }, 100);
            }
        };

        // Wait a bit for ref to be ready
        attachListenersTimeoutRef.current = window.setTimeout(() => {
            attachListenersTimeoutRef.current = null;
            attachListeners();
        }, 0);

        // Cleanup function
        return () => {
            detachListeners();
            stopBoundingBoxUpdates();
            if (attachListenersTimeoutRef.current !== null) {
                clearTimeout(attachListenersTimeoutRef.current);
                attachListenersTimeoutRef.current = null;
            }
        };
    }, [hoverTriggerRef, handleMouseEnter, handleMouseLeave, detachListeners, stopBoundingBoxUpdates]);

    if (!isHovering || !boundingBox) {
        return null;
    }

    return <CustomHighlightOverlay boundingBox={boundingBox} />;
};

